// Update your ListViewContextProps interface to include the last selected ID
import {
  ID,
  calculateIsAllDataSelected,
  calculatedGroupingIsDisabled,
  groupingOnSelect,
  groupingOnSelectAll,
} from '../../../_metronic/helpers'
// Then update your ListViewProvider component
import {
  FC,
  useState,
  createContext,
  useContext,
  useMemo,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react'

// Add lastSelectedId to the interface
interface ListViewContextProps {
  selected: Array<ID>
  setSelected: Dispatch<SetStateAction<Array<ID>>>
  itemIdForUpdate?: ID
  setItemIdForUpdate: (id: ID) => void
  disabled: boolean
  isAllSelected: boolean
  onSelect: (id: ID, isShiftKey: boolean, isRadio?: boolean) => void
  onSelectAll: () => void
  clearSelected: () => void
  lastSelectedId?: ID // New property to track last selected item
}

export const initialListView: ListViewContextProps = {
  selected: [],
  setSelected: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
  clearSelected: () => {},
  setItemIdForUpdate: () => {},
  isAllSelected: false,
  disabled: false,
}

interface Props {
  children: ReactNode
  isLoading: boolean
  data: any[]
}

export const ListViewContext = createContext<ListViewContextProps>(initialListView)

const ListViewProvider: FC<Props> = ({children, isLoading, data}) => {
  const [selected, setSelected] = useState<Array<ID>>(initialListView.selected)
  const [lastSelectedId, setLastSelectedId] = useState<ID | undefined>(undefined)
  const [itemIdForUpdate, setItemIdForUpdate] = useState<ID | undefined>(
    initialListView.itemIdForUpdate
  )
  const disabled = useMemo(() => calculatedGroupingIsDisabled(isLoading, data), [isLoading, data])
  const isAllSelected = useMemo(() => calculateIsAllDataSelected(data, selected), [data, selected])

  // Helper function for range selection
  const selectRange = (startId: ID, endId: ID) => {
    // Find indices of start and end items
    const startIndex = data.findIndex((item) => item.id === startId)
    const endIndex = data.findIndex((item) => item.id === endId)

    if (startIndex === -1 || endIndex === -1) return

    // Determine range bounds
    const minIndex = Math.min(startIndex, endIndex)
    const maxIndex = Math.max(startIndex, endIndex)

    // Get all IDs in the range
    const rangeIds = data.slice(minIndex, maxIndex + 1).map((item) => item.id)

    // Create new selection that includes the range
    const newSelected = [...selected]

    // Add all range IDs that aren't already selected
    rangeIds.forEach((id) => {
      if (!newSelected.includes(id)) {
        newSelected.push(id)
      }
    })

    setSelected(newSelected)
  }

  return (
    <ListViewContext.Provider
      value={{
        selected,
        setSelected,
        lastSelectedId,
        itemIdForUpdate,
        setItemIdForUpdate,
        disabled,
        isAllSelected,
        onSelect: (id: ID, isShiftKey: boolean, isRadio?: boolean) => {
          if (!isRadio) {
            if (isShiftKey && lastSelectedId) {
              // Shift key is pressed, perform range selection
              selectRange(lastSelectedId, id)
            } else {
              // Normal selection
              groupingOnSelect(id, selected, setSelected)
              setLastSelectedId(id) // Update last selected ID
            }
          } else {
            setSelected([id])
          }
        },
        onSelectAll: () => {
          groupingOnSelectAll(isAllSelected, setSelected, data)
          setLastSelectedId(undefined) // Reset last selected ID when selecting all
        },
        clearSelected: () => {
          setSelected([])
          setLastSelectedId(undefined) // Reset last selected ID when clearing selection
        },
      }}
    >
      {children}
    </ListViewContext.Provider>
  )
}

const useListView = () => useContext(ListViewContext)

export {ListViewProvider, useListView}
